/**
 * Feedback Page for Team Leads
 *
 * Allows team leads to submit bug reports, feature requests, and
 * view the status of their submitted feedback.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  Build as ImprovementIcon,
  HelpOutline as QuestionIcon,
  ThumbUp as VoteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { feedbackApi, Feedback, FeedbackCreate } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const feedbackTypeOptions = [
  { value: 'bug_report', label: 'Bug Report', icon: <BugIcon /> },
  { value: 'feature_request', label: 'Feature Request', icon: <FeatureIcon /> },
  { value: 'improvement', label: 'Improvement', icon: <ImprovementIcon /> },
  { value: 'question', label: 'Question', icon: <QuestionIcon /> },
  { value: 'other', label: 'Other', icon: null },
];

const priorityOptions = [
  { value: 'low', label: 'Low', color: 'default' as const },
  { value: 'medium', label: 'Medium', color: 'info' as const },
  { value: 'high', label: 'High', color: 'warning' as const },
  { value: 'critical', label: 'Critical', color: 'error' as const },
];

const statusColors: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  new: 'default',
  under_review: 'info',
  planned: 'info',
  in_progress: 'warning',
  resolved: 'success',
  declined: 'error',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function FeedbackPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Form state
  const [formData, setFormData] = useState<FeedbackCreate>({
    feedback_type: 'feature_request',
    title: '',
    description: '',
    priority: 'medium',
    page_url: window.location.href,
  });

  // Fetch my feedback
  const { data: myFeedback, isLoading: myFeedbackLoading } = useQuery({
    queryKey: ['my-feedback', user?.email],
    queryFn: () => feedbackApi.list({ submitted_by: user?.email, page_size: 100 }).then((res) => res.data),
    enabled: !!user?.email,
  });

  // Fetch all feedback (for browsing)
  const { data: allFeedback, isLoading: allFeedbackLoading } = useQuery({
    queryKey: ['all-feedback', typeFilter, statusFilter, priorityFilter],
    queryFn: () => feedbackApi.list({
      page_size: 50,
      type: typeFilter || undefined,
      status: statusFilter || undefined,
    }).then((res) => res.data),
  });

  // Client-side search + priority filtering
  const filteredAllFeedback = (() => {
    if (!allFeedback?.items) return allFeedback?.items;
    let items = allFeedback.items;
    if (feedbackSearch) {
      const q = feedbackSearch.toLowerCase();
      items = items.filter((f) =>
        f.title.toLowerCase().includes(q) || f.description?.toLowerCase().includes(q),
      );
    }
    if (priorityFilter) {
      items = items.filter((f) => f.priority === priorityFilter);
    }
    return items;
  })();

  const hasActiveFilters = typeFilter !== '' || statusFilter !== '' || priorityFilter !== '';

  const handleClearFeedbackFilters = () => {
    setFeedbackSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setPriorityFilter('');
  };

  // Create feedback mutation
  const createMutation = useMutation({
    mutationFn: (data: FeedbackCreate) =>
      feedbackApi.create(data, user?.email || '', user?.displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-feedback'] });
      queryClient.invalidateQueries({ queryKey: ['all-feedback'] });
      setDialogOpen(false);
      setFormData({
        feedback_type: 'feature_request',
        title: '',
        description: '',
        priority: 'medium',
        page_url: window.location.href,
      });
      setSnackbar({ open: true, message: 'Feedback submitted successfully!', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to submit: ${error.message}`, severity: 'error' });
    },
  });

  // Vote mutation
  const voteMutation = useMutation({
    mutationFn: (feedbackId: string) => feedbackApi.vote(feedbackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-feedback'] });
      setSnackbar({ open: true, message: 'Vote recorded!', severity: 'success' });
    },
  });

  const handleSubmit = () => {
    if (!formData.title || !formData.description) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }
    createMutation.mutate(formData);
  };

  const handleViewFeedback = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setViewDialogOpen(true);
  };

  const renderFeedbackTable = (items: Feedback[] | undefined, showSubmitter: boolean = false) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Title</TableCell>
            {showSubmitter && <TableCell>Submitted By</TableCell>}
            <TableCell>Priority</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Votes</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items?.map((feedback) => (
            <TableRow key={feedback.id}>
              <TableCell>
                <Chip
                  label={feedback.feedback_type.replace('_', ' ')}
                  size="small"
                  icon={feedbackTypeOptions.find((t) => t.value === feedback.feedback_type)?.icon || undefined}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {feedback.title}
                </Typography>
                {feedback.admin_response && (
                  <Chip
                    label="Admin responded"
                    size="small"
                    color="success"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </TableCell>
              {showSubmitter && (
                <TableCell>
                  <Typography variant="body2">{feedback.submitted_by_name || feedback.submitted_by_email}</Typography>
                </TableCell>
              )}
              <TableCell>
                <Chip
                  label={feedback.priority}
                  size="small"
                  color={priorityOptions.find((p) => p.value === feedback.priority)?.color || 'default'}
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={feedback.status.replace('_', ' ')}
                  size="small"
                  color={statusColors[feedback.status] || 'default'}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{feedback.vote_count}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{new Date(feedback.created_at).toLocaleDateString()}</Typography>
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => handleViewFeedback(feedback)}
                  title="View details"
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
                {showSubmitter && (
                  <IconButton
                    size="small"
                    onClick={() => voteMutation.mutate(feedback.id)}
                    disabled={voteMutation.isPending}
                    title="Vote"
                  >
                    <VoteIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
          {items?.length === 0 && (
            <TableRow>
              <TableCell colSpan={showSubmitter ? 8 : 7} align="center">
                No feedback found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Feedback & Feature Requests</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Submit Feedback
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Have a bug to report, a feature to request, or an improvement to suggest? Submit your feedback here and
            we'll review it. You can also vote on other users' feedback to help prioritize features.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label={`My Feedback (${myFeedback?.items.length || 0})`} />
          <Tab label="All Feedback" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {myFeedbackLoading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : (
          renderFeedbackTable(myFeedback?.items)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Search bar */}
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Search feedback by title or description..."
              value={feedbackSearch}
              onChange={(e) => setFeedbackSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
              <IconButton size="small" onClick={() => setFiltersOpen(!filtersOpen)} color={filtersOpen || hasActiveFilters ? 'primary' : 'default'}>
                <FilterIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Filters (collapsible) */}
        <Collapse in={filtersOpen}>
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} label="Type">
                    <MenuItem value="">All Types</MenuItem>
                    {feedbackTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                    <MenuItem value="">All Statuses</MenuItem>
                    {Object.keys(statusColors).map((s) => (
                      <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} label="Priority">
                    <MenuItem value="">All Priorities</MenuItem>
                    {priorityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFeedbackFilters} disabled={!hasActiveFilters && !feedbackSearch}>
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {allFeedbackLoading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : (
          renderFeedbackTable(filteredAllFeedback, true)
        )}
      </TabPanel>

      {/* Submit Feedback Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.feedback_type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, feedback_type: e.target.value })}
                >
                  {feedbackTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  {priorityOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Title"
                fullWidth
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief summary of your feedback"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                required
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Provide detailed information about your bug report, feature request, or suggestion..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Feedback Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {feedbackTypeOptions.find((t) => t.value === selectedFeedback?.feedback_type)?.icon}
            <Typography variant="h6">{selectedFeedback?.title}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Status and Priority */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={selectedFeedback.feedback_type.replace('_', ' ')}
                  size="small"
                />
                <Chip
                  label={selectedFeedback.priority}
                  size="small"
                  color={priorityOptions.find((p) => p.value === selectedFeedback.priority)?.color || 'default'}
                />
                <Chip
                  label={selectedFeedback.status.replace('_', ' ')}
                  size="small"
                  color={statusColors[selectedFeedback.status] || 'default'}
                />
              </Box>

              {/* Description */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.description}
                  </Typography>
                </Paper>
              </Box>

              {/* Admin Response */}
              {selectedFeedback.admin_response && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Admin Response
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedFeedback.admin_response}
                    </Typography>
                    {selectedFeedback.responded_by && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        — {selectedFeedback.responded_by}
                        {selectedFeedback.responded_at && ` on ${new Date(selectedFeedback.responded_at).toLocaleDateString()}`}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Meta info */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Submitted
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedFeedback.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Votes
                  </Typography>
                  <Typography variant="body2">{selectedFeedback.vote_count}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Submitted By
                  </Typography>
                  <Typography variant="body2">
                    {selectedFeedback.submitted_by_name || selectedFeedback.submitted_by_email}
                  </Typography>
                </Grid>
                {selectedFeedback.updated_at !== selectedFeedback.created_at && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedFeedback.updated_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<VoteIcon />}
            onClick={() => {
              if (selectedFeedback) {
                voteMutation.mutate(selectedFeedback.id);
              }
            }}
            disabled={voteMutation.isPending}
          >
            Vote for this
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
